/**
 * Core Allocation Math Engine
 * Shared between the Backend Route and the Frontend Simulator
 * to ensure 100% fidelity.
 */

export function calculateAllocations(actualDeposit, incomeFrequency, allocations) {
  const fundUpdates = {};
  let currentRemaining = actualDeposit;
  const dryNodes = new Set(); 
  const ruleResults = [];

  for (const a of allocations) {
    if (currentRemaining <= 0 && a.type !== 'remainder') {
        dryNodes.add(a.fund_id);
        ruleResults.push({
          id: a.id,
          fund_id: a.fund_id,
          type: a.type,
          expectedAmount: a.type === 'fixed' ? parseFloat(a.amount) : 0,
          actualAmount: 0,
          status: 'STARVED'
        });
        continue;
    }

    let expectedAmount = 0;
    let finalAmt = 0;

    if (a.type === 'fixed') {
      let amt = parseFloat(a.amount);
      
      // Smart Funding Algorithm
      if (a.due_day) {
        const today = new Date();
        const nextDueDate = new Date(today.getFullYear(), today.getMonth(), parseInt(a.due_day));
        if (nextDueDate < today) {
            nextDueDate.setMonth(nextDueDate.getMonth() + 1); // due next month
        }
        
        const nextPaycheck = new Date(today);
        const freq = (incomeFrequency || 'bi-weekly').toLowerCase();
        if (freq === 'weekly') nextPaycheck.setDate(today.getDate() + 7);
        else if (freq === 'bi-weekly') nextPaycheck.setDate(today.getDate() + 14);
        else if (freq === 'semi-monthly') nextPaycheck.setDate(today.getDate() + 15);
        else if (freq === 'monthly') nextPaycheck.setMonth(today.getMonth() + 1);
        
        if (nextDueDate <= nextPaycheck) {
            amt = parseFloat(a.amount); 
        } else {
            if (freq === 'weekly') amt = parseFloat(a.amount) / 4;
            else if (freq === 'bi-weekly' || freq === 'semi-monthly') amt = parseFloat(a.amount) / 2;
            else amt = parseFloat(a.amount);
        }
      }
      
      expectedAmount = Math.round(amt * 100) / 100;
      finalAmt = Math.min(expectedAmount, currentRemaining);
      
    } else if (a.type === 'percentage') {
      const percentage = parseFloat(a.amount) / 100;
      expectedAmount = Math.round((actualDeposit * percentage) * 100) / 100;
      finalAmt = Math.min(expectedAmount, currentRemaining);
      
    } else if (a.type === 'remainder') {
      expectedAmount = currentRemaining;
      finalAmt = currentRemaining;
    }

    finalAmt = Math.round(finalAmt * 100) / 100;

    if (finalAmt <= 0 && a.type !== 'remainder') {
        dryNodes.add(a.fund_id);
        ruleResults.push({
          id: a.id,
          fund_id: a.fund_id,
          type: a.type,
          expectedAmount,
          actualAmount: 0,
          status: 'STARVED'
        });
        continue;
    }

    const targetAmount = parseFloat(a.target_amount || 0);
    const currentBalance = parseFloat(a.current_balance || 0);
    let excess = 0;
    
    // Cap Checking
    let wasCapped = false;
    if (targetAmount > 0) {
      const currentPlannedDeposit = fundUpdates[a.fund_id] || 0;
      const roomLeft = Math.round(Math.max(0, targetAmount - (currentBalance + currentPlannedDeposit)) * 100) / 100;
      if (finalAmt > roomLeft) {
        excess = Math.round((finalAmt - roomLeft) * 100) / 100;
        finalAmt = roomLeft;
        wasCapped = true;
      }
    }

    currentRemaining = Math.round((currentRemaining - finalAmt) * 100) / 100;
    fundUpdates[a.fund_id] = Math.round(((fundUpdates[a.fund_id] || 0) + finalAmt) * 100) / 100;
    
    let status = 'FULLY_FUNDED';
    if (wasCapped) status = 'CAPPED';
    else if (finalAmt < expectedAmount && a.type !== 'remainder') status = 'STARVED';

    ruleResults.push({
      id: a.id,
      fund_id: a.fund_id,
      type: a.type,
      expectedAmount,
      actualAmount: finalAmt,
      status
    });

    // Handle Spillover
    if (excess > 0 && a.rollover_fund_id) {
      const actualSpill = Math.min(excess, currentRemaining);
      if (actualSpill > 0) {
        currentRemaining = Math.round((currentRemaining - actualSpill) * 100) / 100;
        fundUpdates[a.rollover_fund_id] = Math.round(((fundUpdates[a.rollover_fund_id] || 0) + actualSpill) * 100) / 100;
      }
    }
  }

  return { 
    fundUpdates, 
    currentRemaining, 
    dryNodes: Array.from(dryNodes),
    ruleResults
  };
}
